import { FlowObjectType, FlowObjectDefinition } from '../../../../models/flow-object.model';
import { Component, OnInit, Input, EventEmitter, Output, ViewChild, OnChanges, signal, NO_ERRORS_SCHEMA } from '@angular/core';
import { ConfigSchema, ConfigSchemaSectionAccessLevel, ConfigSchemaSectionDocumentClass, ConfigSchemaTaskAcknowledge, ConfigSchemaTaskApprove, ConfigSchemaTaskForm } from '../../../../models/config-schema.model';
import { AccessLevelType } from '../../../../models/edocs.model';
import { FlowDefinition } from '../../../../models/flow.model';
import { Utils } from '../../../../shared/utils';
import { AccessLevelComponent } from '../_edocs-fields/access-level/access-level.component';
import { IMPORTS } from './_imports';

@Component({
    selector: 'fod-document',
    templateUrl: './fod-document.component.html',
    styleUrl: './fod-document.component.scss',
    standalone: true,
    imports: IMPORTS,
    schemas: [NO_ERRORS_SCHEMA]
})
export class FlowObjectDetailsDocumentComponent implements OnInit, OnChanges {
    // #region [ViewChild]
    @ViewChild('accessLevelRef') accessLevelRef: AccessLevelComponent;
    // #endregion

    // #region [Type props]
    FlowObjectType = FlowObjectType;
    AccessLevelType = AccessLevelType;
    Utils = Utils;
    // #endregion

    // #region [signals]
    $configSchema = signal(null as ConfigSchema);
    $flowDefinition = signal(null as FlowDefinition);
    $isReadOnlyMode = signal(false);
    $configSchemaSectionAccessLevel = signal(new ConfigSchemaSectionAccessLevel());
    $configSchemaSectionDocumentClass = signal(new ConfigSchemaSectionDocumentClass());
    // #endregion

    // #region [Input/Output]
    @Input() _configSchema: ConfigSchema;
    @Input() _flowDefinition: FlowDefinition;
    @Input() _flowObjectDefinition: FlowObjectDefinition;
    @Input() _isReadOnlyMode: boolean;
    @Output() _configSchemaChangeEvent = new EventEmitter<ConfigSchema>();
    // #endregion

    constructor() { }

    ngOnInit() {
        this.resolveConfigSchema();

        for (let prop in this.$configSchemaSectionAccessLevel()) {
            Utils.updSignal(this.$configSchemaSectionAccessLevel, x => {
                if (this._flowObjectDefinition.typeId == FlowObjectType.TaskAcknowledge) {
                    x[prop] = this.$configSchema().taskAcknowledge.accessLevel[prop] || x[prop];
                } else if (this._flowObjectDefinition.typeId == FlowObjectType.GatewayApprove) {
                    x[prop] = this.$configSchema().taskApprove.accessLevel[prop] || x[prop];
                } else {
                    x[prop] = this.$configSchema().taskForm.accessLevel[prop] || x[prop];
                }
                return x;
            });
        }

        for (let prop in this.$configSchemaSectionDocumentClass()) {
            Utils.updSignal(this.$configSchemaSectionDocumentClass, x => {
                if (this._flowObjectDefinition.typeId == FlowObjectType.TaskAcknowledge) {
                    x[prop] = this.$configSchema().taskAcknowledge.documentClass[prop] || x[prop];
                } else if (this._flowObjectDefinition.typeId == FlowObjectType.GatewayApprove) {
                    x[prop] = this.$configSchema().taskApprove.documentClass[prop] || x[prop];
                } else {
                    x[prop] = this.$configSchema().taskForm.documentClass[prop] || x[prop];
                }
                return x;
            });
        }
    }

    ngOnChanges() {
        this.$configSchema.set(this._configSchema);
        this.$flowDefinition.set(this._flowDefinition);
        this.$isReadOnlyMode.set(this._isReadOnlyMode);
    }

    updateSourceCode(forcedConfigSchema: ConfigSchema) {
        Utils.setSignal(this.$configSchema, forcedConfigSchema);

        if (this._flowObjectDefinition.typeId == FlowObjectType.TaskAcknowledge) {
            Utils.setSignal(this.$configSchemaSectionAccessLevel, Object.assign(this.$configSchemaSectionAccessLevel(), this.$configSchema().taskAcknowledge.accessLevel));

            // limpando deliberadamente as configurações de Classe enquanto o E-Docs não deixar os GUIDs
            // de classe documental únicos dentre todos os ambientes, a exemplo dos do Organograma
            //Utils.setSignal(this.$configSchemaSectionDocumentClass, Object.assign(this.$configSchemaSectionDocumentClass(), this.$configSchema().taskAcknowledge.documentClass));
            Utils.setSignal(this.$configSchemaSectionDocumentClass, new ConfigSchemaSectionDocumentClass());
            Utils.updSignal(this.$configSchema, x => {
                x.taskAcknowledge.documentClass = new ConfigSchemaSectionDocumentClass();
                return x;
            });
        } else if (this._flowObjectDefinition.typeId == FlowObjectType.GatewayApprove) {
            Utils.setSignal(this.$configSchemaSectionAccessLevel, Object.assign(this.$configSchemaSectionAccessLevel(), this.$configSchema().taskApprove.accessLevel));

            // limpando deliberadamente as configurações de Classe enquanto o E-Docs não deixar os GUIDs
            // de classe documental únicos dentre todos os ambientes, a exemplo dos do Organograma
            //Utils.setSignal(this.$configSchemaSectionDocumentClass, Object.assign(this.$configSchemaSectionDocumentClass(), this.$configSchema().taskApprove.documentClass));
            Utils.setSignal(this.$configSchemaSectionDocumentClass, new ConfigSchemaSectionDocumentClass());
            Utils.updSignal(this.$configSchema, x => {
                x.taskApprove.documentClass = new ConfigSchemaSectionDocumentClass();
                return x;
            });
        } else {
            Utils.setSignal(this.$configSchemaSectionAccessLevel, Object.assign(this.$configSchemaSectionAccessLevel(), this.$configSchema().taskForm.accessLevel));

            // limpando deliberadamente as configurações de Classe enquanto o E-Docs não deixar os GUIDs
            // de classe documental únicos dentre todos os ambientes, a exemplo dos do Organograma
            //Utils.setSignal(this.$configSchemaSectionDocumentClass, Object.assign(this.$configSchemaSectionDocumentClass(), this.$configSchema().taskForm.documentClass));
            Utils.setSignal(this.$configSchemaSectionDocumentClass, new ConfigSchemaSectionDocumentClass());
            Utils.updSignal(this.$configSchema, x => {
                x.taskForm.documentClass = new ConfigSchemaSectionDocumentClass();
                return x;
            });
        }

        this.accessLevelRef?.initLegalReasoningsInfo();

        this._configSchemaChangeEvent.emit(this.$configSchema());
    }

    onUpdateAccessLevel(event: ConfigSchemaSectionAccessLevel) {
        Utils.setSignal(this.$configSchemaSectionAccessLevel, Object.assign(this.$configSchemaSectionAccessLevel(), event));

        if (this._flowObjectDefinition.typeId == FlowObjectType.TaskAcknowledge) {
            Utils.updSignal(this.$configSchema, x => {
                Object.assign(x.taskAcknowledge.accessLevel, this.$configSchemaSectionAccessLevel());
                return x;
            });
        } else if (this._flowObjectDefinition.typeId == FlowObjectType.GatewayApprove) {
            Utils.updSignal(this.$configSchema, x => {
                Object.assign(x.taskApprove.accessLevel, this.$configSchemaSectionAccessLevel());
                return x;
            });
        } else {
            Utils.updSignal(this.$configSchema, x => {
                Object.assign(x.taskForm.accessLevel, this.$configSchemaSectionAccessLevel());
                return x;
            });
        }

        this._configSchemaChangeEvent.emit(this.$configSchema());
    }

    onUpdateDocumentClass(event: ConfigSchemaSectionDocumentClass) {
        Utils.setSignal(this.$configSchemaSectionDocumentClass, Object.assign(this.$configSchemaSectionDocumentClass(), event));

        if (this._flowObjectDefinition.typeId == FlowObjectType.TaskAcknowledge) {
            Utils.updSignal(this.$configSchema, x => {
                Object.assign(x.taskAcknowledge.documentClass, this.$configSchemaSectionDocumentClass());
                return x;
            });
        } else if (this._flowObjectDefinition.typeId == FlowObjectType.GatewayApprove) {
            Utils.updSignal(this.$configSchema, x => {
                Object.assign(x.taskApprove.documentClass, this.$configSchemaSectionDocumentClass());
                return x;
            });
        } else {
            Utils.updSignal(this.$configSchema, x => {
                Object.assign(x.taskForm.documentClass, this.$configSchemaSectionDocumentClass());
                return x;
            });
        }

        this._configSchemaChangeEvent.emit(this.$configSchema());
    }

    // #region [===== private methods =====]
    // #endregion

    private resolveConfigSchema() {
        Utils.updSignal(this.$configSchema, x => {
            x.taskApprove = x.taskApprove || new ConfigSchemaTaskApprove();
            x.taskAcknowledge = x.taskAcknowledge || new ConfigSchemaTaskAcknowledge();
            x.taskForm = x.taskForm || new ConfigSchemaTaskForm();
            return x;
        });

        if (this._flowObjectDefinition.typeId != FlowObjectType.StartForm) {
            if (this._flowObjectDefinition.typeId == FlowObjectType.GatewayApprove) {
                if (
                    Utils.areEqualObjects(this.$configSchema().taskApprove, new ConfigSchemaTaskApprove())
                    && !Utils.areEqualObjects(this.$configSchema().taskForm, new ConfigSchemaTaskForm())
                ) {
                    Utils.updSignal(this.$configSchema, x => {
                        Object.assign(x.taskApprove, x.taskForm);
                        return x;
                    });
                }
            } else if (this._flowObjectDefinition.typeId == FlowObjectType.TaskAcknowledge) {
                if (
                    Utils.areEqualObjects(this.$configSchema().taskAcknowledge, new ConfigSchemaTaskAcknowledge())
                    && !Utils.areEqualObjects(this.$configSchema().taskForm, new ConfigSchemaTaskForm())
                ) {
                    Utils.updSignal(this.$configSchema, x => {
                        Object.assign(x.taskAcknowledge, x.taskForm);
                        return x;
                    });
                }
            }

            Utils.updSignal(this.$configSchema, x => {
                x.taskForm = new ConfigSchemaTaskForm();
                return x;
            });
        }
    }
}
